import { LAND_MASK_BASE64, MAP_COLS, MAP_ROWS } from './worldMapDots.generated';

/**
 * Any point in normalized map space. `x` is 0..1 left to right, `y` is 0..1
 * top to bottom.
 *
 * Fields are readonly because `LAND_DOTS` is a process-wide singleton shared
 * by every mount — a stray write would corrupt the map for all of them.
 */
export interface NormalizedPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * A point that is specifically a land cell of the shipped map — i.e. a member
 * of `LAND_DOTS`. Structurally identical to `NormalizedPoint`; the distinct
 * name keeps "a dot on the map" from being confused with "any projected
 * coordinate", which is what `projectToMap` returns.
 *
 * `x` is a cell center so it is always strictly inside (0, 1); `y` runs edge
 * to edge and does reach both 0 and 1.
 */
export type MapDot = NormalizedPoint;

/**
 * Expands the packed land bitmask into dot coordinates. One bit per grid cell,
 * row-major, most significant bit first — the same order the generator writes.
 *
 * The two axes are deliberately NOT symmetric:
 *   x is the center of its cell (hence +0.5, divided by `cols`), so columns
 *     tile evenly and no dot sits on the seam where the map wraps.
 *   y is edge to edge (no offset, divided by `rows - 1`), so row 0 lands on
 *     the top edge and the last row on the bottom edge.
 * Making them match would shift the entire map. Leave it alone.
 *
 * `rows` must be at least 2; a single-row grid would divide by zero. Every
 * real caller passes the generated 168x63 grid.
 */
export function decodeLandMask(base64: string, cols: number, rows: number): MapDot[] {
  const binary = atob(base64);
  const dots: MapDot[] = [];

  for (let i = 0; i < cols * rows; i += 1) {
    const byteIndex = i >> 3;
    const bitMask = 128 >> (i & 7); // 128 = MSB first
    if (!(binary.charCodeAt(byteIndex) & bitMask)) continue;

    dots.push({
      x: ((i % cols) + 0.5) / cols,
      y: Math.floor(i / cols) / (rows - 1),
    });
  }

  return dots;
}

/** Decoded once at module load and shared by every mount. */
export const LAND_DOTS: readonly MapDot[] = decodeLandMask(
  LAND_MASK_BASE64,
  MAP_COLS,
  MAP_ROWS
);
